import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { ABOUT as TIDS } from "@/constants/testIds";
import { COMPANY, VALUES, CERTIFICATIONS, TRUST_ITEMS, INTERMEDIARY_NOTICE as INTERMEDIARY_NOTICE_TEXT } from "@/config/site";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

export default function AboutPage() {
  useSEO({
    title: "À propos — Plateforme locale de mise en relation (37)",
    description:
      "PENOW, plateforme locale de mise en relation basée à Fondettes (37230) : notre histoire, nos valeurs, notre réseau de professionnels partenaires du bâtiment en Indre-et-Loire.",
    keywords:
      "plateforme mise en relation Fondettes, apporteur d'affaires bâtiment 37, réseau artisans partenaires 37, PENOW histoire, réseau RGE QualiPAC Tours",
    path: "/a-propos",
  });
  return (
    <div data-testid={TIDS.section}>
      {/* Header */}
      <section className="container-editorial pt-16 md:pt-24 pb-8">
        <div className="max-w-3xl">
          <div className="eyebrow mb-6">À propos de PENOW</div>
          <h1 className="font-display text-5xl md:text-6xl leading-tight text-anthracite">
            Le bon pro,
            <br />
            <span className="text-terracotta">au bon moment.</span>
          </h1>
          <p className="mt-8 text-lg text-anthracite/70 leading-relaxed">
            PENOW est né d'une conviction : trouver le bon professionnel pour
            son projet ne devrait pas être un parcours du combattant. Depuis
            plus de {COMPANY.yearsExperience} ans, nous mettons en relation
            particuliers et professionnels avec un réseau d'artisans qualifiés
            en région Centre-Val de Loire, sélectionnés selon des critères
            stricts.
          </p>
          <p className="mt-4 text-sm text-anthracite/60 leading-relaxed max-w-2xl">
            <strong>Notre rôle</strong> : {INTERMEDIARY_NOTICE_TEXT} Nous ne
            réalisons pas les travaux, nous vous connectons avec le professionnel
            partenaire adapté à votre projet, et vous accompagnons dans les
            démarches d'aides éventuelles.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.22em] text-anthracite/50">
            <span>Basés à Fondettes (37)</span>
            <span className="w-1 h-1 bg-anthracite/30 rounded-full" />
            <span>{COMPANY.yearsExperience}+ ans de réseau</span>
            <span className="w-1 h-1 bg-anthracite/30 rounded-full" />
            <span>Partenaires RGE certifiés</span>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="container-editorial py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <Reveal
              key={v.key}
              delay={i * 90}
              data-testid={TIDS.valueCard}
              className="border border-black/10 bg-white p-8 md:p-10 hover-lift hover:border-copper"
            >
              <div className="font-display text-terracotta text-lg">0{i + 1}</div>
              <h3 className="mt-4 font-display text-2xl text-anthracite">{v.title}</h3>
              <p className="mt-4 text-anthracite/60 leading-relaxed">{v.description}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Trust stats band */}
      <section className="bg-deep-blue text-white">
        <div className="container-editorial py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {TRUST_ITEMS.map((t) => (
            <div key={t.label} className="border-l border-white/15 pl-5">
              <div className="font-display text-3xl md:text-4xl">{t.value}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/50">{t.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications */}
      <section className="container-editorial py-20 md:py-28" data-testid={TIDS.certifications}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Notre réseau & nos engagements</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-anthracite">
              Un réseau d'experts, une exigence.
            </h2>
            <p className="mt-6 text-anthracite/60 leading-relaxed">
              PENOW s'appuie sur un réseau d'artisans partenaires soigneusement
              sélectionnés et certifiés, pour vous offrir un niveau de qualité
              constant et l'accès aux aides financières de l'État sur les
              prestations éligibles.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CERTIFICATIONS.map((c) => (
              <div key={c} className="flex items-center gap-3 border border-black/10 bg-white p-4">
                <div className="w-10 h-10 flex items-center justify-center bg-terracotta/10 text-terracotta">
                  <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="text-sm text-anthracite font-medium">{c}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center gap-4">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-6 py-4 tracking-wide transition-colors"
            data-testid="about-cta-quote"
          >
            Discutons de votre projet
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <Link to="/realisations" className="link-arrow text-anthracite text-sm">
            Voir les réalisations de nos partenaires
          </Link>
        </div>
      </section>
    </div>
  );
}
